import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import "./procedureSchedule.scss";
import { HMS_IFRAME_MESSAGES } from "./iframeMessages";
import { patientid } from "../../Redux/Slice/rootSlice";

const PROCEDURE_URL =
  process.env.REACT_APP_PROCEDURE_URL ||
  (process.env.NODE_ENV === "development" ? "http://localhost:5177" : "/procedures-module");

const PROCEDURE_ORIGIN = PROCEDURE_URL.startsWith("http")
  ? new URL(PROCEDURE_URL).origin
  : window.location.origin;

function parseStoredUser(userdata) {
  if (!userdata) return null;
  try {
    return JSON.parse(userdata);
  } catch (e) {
    return null;
  }
}

function ProcedureSchedule() {
  const iframeRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const sideactive = useSelector((state) => state.counter.sidebarnav);
  const [isLoading, setIsLoading] = useState(true);

  const procedurePath = useMemo(() => {
    return (
      location.pathname.replace(/^\/(inpatient|procedures|procedure-schedule)/, "") || "/"
    );
  }, [location.pathname]);

  const iframeSrc = `${PROCEDURE_URL.replace(/\/$/, "")}${procedurePath}${location.search}`;

  useEffect(() => {
    setIsLoading(true);
  }, [iframeSrc]);

  const sendAuthToIframe = () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return false;

    const token = localStorage.getItem("token");
    const userdata = localStorage.getItem("userdata");
    const role = localStorage.getItem("role") || parseStoredUser(userdata)?.role || "";
    const branch = localStorage.getItem("selectedBranch") || localStorage.getItem("branch");
    if (!token) return false;

    try {
      const authPayload = {
        type: "HMS_AUTH_TOKEN",
        token,
        user: parseStoredUser(userdata),
        role,
        branch,
      };
      iframe.contentWindow.postMessage(authPayload, PROCEDURE_ORIGIN);
      iframe.contentWindow.postMessage({ ...authPayload, type: "HMS_INIT_AUTH" }, PROCEDURE_ORIGIN);
      return true;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.origin === PROCEDURE_ORIGIN &&
        event.source === iframeRef.current?.contentWindow
      ) {
        if (event.data?.type === HMS_IFRAME_MESSAGES.ready || event.data?.type === "PROCEDURE_READY") {
          sendAuthToIframe();
          setIsLoading(false);
        } else if (event.data?.type === HMS_IFRAME_MESSAGES.authRequest || event.data?.type === "PROCEDURE_AUTH_REQUEST") {
          sendAuthToIframe();
        } else if ((event.data?.type === HMS_IFRAME_MESSAGES.navigate || event.data?.type === "HMS_NAVIGATE") && event.data?.path) {
          if (event.data?.patientId) {
            dispatch(patientid(event.data.patientId));
            localStorage.setItem("patientid", event.data.patientId);
          }
          navigate(event.data.path);
        } else if (event.data?.type === "HMS_UNAUTHORIZED") {
          localStorage.clear();
          window.location.href = "/login";
        }
      }
    };

    window.addEventListener("message", handleMessage);

    const fallbackTimer = setTimeout(() => {
      sendAuthToIframe();
      setIsLoading(false);
    }, 2500);

    return () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(fallbackTimer);
    };
  }, [navigate]);

  return (
    <div className={`procedure-container ${sideactive ? "sidebar-open" : "sidebar-closed"}`}>
      {isLoading && (
        <div className="procedure-loader" role="status" aria-live="polite">
          <div className="procedure-loader-card">
            <span className="procedure-loader-spinner" aria-hidden="true" />
            <strong>Loading Procedure List...</strong>
            <span>Syncing schedules across operating facilities and centers.</span>
          </div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title="Procedure List / Patient List"
        className="procedure-iframe"
        onLoad={sendAuthToIframe}
      />
    </div>
  );
}

export default ProcedureSchedule;
